import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api, { API } from "@/lib/api";
import { formatRupees, formatRupeesCompact, formatPercent, formatDate, todayISO, INVESTMENT_TYPE_LABEL } from "@/lib/format";
import { Plus, Upload, ArrowLeft, ExternalLink, Trash2, UserPlus, Pencil } from "lucide-react";
import PerformanceView from "@/components/PerformanceView";
import { ValuationPanel, ValuationDialog } from "@/components/ValuationPanel";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const CATEGORIES = ["interest", "rent", "profit_share", "principal", "sale", "other", "investment", "distribution"];

export default function InvestmentDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.role === "accounts";
  const [inv, setInv] = useState(null);
  const [txns, setTxns] = useState([]);
  const [vals, setVals] = useState([]);
  const [docs, setDocs] = useState([]);
  const [txDialog, setTxDialog] = useState(false);
  const [valDialog, setValDialog] = useState(false);
  const [txForm, setTxForm] = useState({ direction: "in", amount: "", date: todayISO(), category: "interest", note: "" });
  const [valForm, setValForm] = useState({ value: "", date: todayISO(), note: "", method: "manual", inputs: {} });

  const load = async () => {
    const [i, t, v, d] = await Promise.all([
      api.get(`/investments/${id}`),
      api.get(`/investments/${id}/transactions`),
      api.get(`/investments/${id}/valuations`),
      api.get(`/investments/${id}/documents`),
    ]);
    setInv(i.data); setTxns(t.data); setVals(v.data); setDocs(d.data);
  };

  useEffect(() => { load(); }, [id]);

  const addTx = async () => {
    try {
      await api.post(`/investments/${id}/transactions`, {
        investment_id: id, ...txForm, amount: Number(txForm.amount),
      });
      toast.success("Transaction added");
      setTxDialog(false);
      setTxForm({ direction: "in", amount: "", date: todayISO(), category: "interest", note: "" });
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const addVal = async () => {
    try {
      await api.post(`/investments/${id}/valuations`, {
        as_of_date: valForm.date,
        method: valForm.method || "manual",
        value: valForm.method === "manual" ? Number(valForm.value || 0) : null,
        inputs: valForm.inputs || {},
        notes: valForm.note || "",
      });
      toast.success("Valuation snapshot saved");
      setValDialog(false);
      setValForm({ value: "", date: todayISO(), note: "", method: "manual", inputs: {} });
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const uploadDoc = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const fd = new FormData();
    fd.append("file", f);
    try {
      await api.post(`/investments/${id}/documents`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document uploaded");
      await load();
    } catch (err) { toast.error("Upload failed"); }
    e.target.value = "";
  };

  const deleteDoc = async (docId) => {
    if (!window.confirm("Delete this document?")) return;
    await api.delete(`/documents/${docId}`);
    toast.success("Removed");
    await load();
  };

  const deleteTx = async (tid) => {
    if (!window.confirm("Delete this transaction?")) return;
    await api.delete(`/transactions/${tid}`);
    toast.success("Deleted");
    await load();
  };

  if (!inv) return <div className="text-slate-500 text-sm">Loading...</div>;

  const meta = inv.meta || {};
  const token = localStorage.getItem("mv_token");

  return (
    <div className="space-y-8">
      <Link to="/investments" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900" data-testid="back-to-investments">
        <ArrowLeft className="h-4 w-4" /> Back to investments
      </Link>

      <div>
        <span className="inline-block text-[10px] tracking-[0.25em] uppercase px-2 py-0.5 bg-red-50 text-red-700">
          {INVESTMENT_TYPE_LABEL[inv.type]}
        </span>
        <h1 className="mt-3 font-editorial font-light text-[34px] leading-[1.05] sm:text-4xl md:text-5xl text-slate-900 tracking-[-0.035em]" data-testid="investment-name">
          {inv.name}
        </h1>
        <div className="mt-3 text-sm text-slate-500">Invested on {formatDate(inv.invested_at)}</div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        {[
          ["Invested", inv.total_invested],
          ["Current", inv.current_value],
          ["Profit", inv.profit],
          ["Return", formatPercent(inv.return_percent), true],
        ].map(([label, val, isPct], i) => (
          <div key={i} className="border border-slate-200 p-5 bg-white">
            <div className="text-[10px] tracking-wider uppercase text-slate-500">{label}</div>
            <div className={`mt-2 font-mono-fin text-2xl font-semibold ${
              label === "Profit" ? (val >= 0 ? "text-green-700" : "text-red-600") :
              label === "Return" ? (inv.return_percent >= 0 ? "text-green-700" : "text-red-600") : "text-slate-900"
            }`}>
              {isPct ? val : formatRupeesCompact(val)}
            </div>
          </div>
        ))}
      </div>

      <Tabs defaultValue="details">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="details" data-testid="tab-details">Details</TabsTrigger>
          <TabsTrigger value="performance" data-testid="tab-performance">Performance</TabsTrigger>
          <TabsTrigger value="txns" data-testid="tab-txns">Transactions</TabsTrigger>
          <TabsTrigger value="valuations" data-testid="tab-valuations">Valuations</TabsTrigger>
          <TabsTrigger value="investors" data-testid="tab-investors">Investors</TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents">Documents</TabsTrigger>
        </TabsList>

        <TabsContent value="performance" className="mt-6">
          <PerformanceView investmentId={id} compact />
        </TabsContent>

        <TabsContent value="details" className="mt-6">
          <div className="grid gap-4 md:grid-cols-3">
            <div className="md:col-span-2 border border-slate-200 p-5 sm:p-6 bg-white rounded-md space-y-3 text-sm">
              {Object.entries(meta).map(([k, v]) => (
                <div key={k} className="grid grid-cols-3 gap-4 py-2 border-b border-slate-100 last:border-0">
                  <div className="text-[10px] tracking-wider uppercase text-slate-500 col-span-1">{k.replace(/_/g, " ")}</div>
                  <div className="col-span-2 text-slate-900">
                    {typeof v === "object" ? JSON.stringify(v) :
                     typeof v === "boolean" ? (v ? "Yes" : "No") :
                     typeof v === "number" && k.match(/price|cost|rent|amount|value|target|commitment/) ? formatRupees(v) : String(v)}
                  </div>
                </div>
              ))}
              {inv.notes && (
                <div className="pt-3">
                  <div className="text-[10px] tracking-wider uppercase text-slate-500 mb-1">Notes</div>
                  <div className="text-slate-700">{inv.notes}</div>
                </div>
              )}
            </div>
            <div className="border border-red-200 bg-red-50/40 rounded-md p-5 space-y-3">
              <div>
                <div className="eyebrow">Expected return</div>
                <div className="mt-1 font-mono-fin text-2xl text-slate-900">
                  {formatPercent(inv.expected_return_percent || 0)}
                </div>
                <div className="text-xs text-slate-500 mt-1">per year</div>
              </div>
              {inv.expected_return_percent > 0 && inv.amount_invested > 0 && (
                <>
                  <div className="pt-3 border-t border-red-200/50">
                    <div className="text-[10px] tracking-wider uppercase text-slate-500">≈ per year</div>
                    <div className="font-mono-fin text-sm text-slate-900 mt-1">
                      {formatRupees(inv.amount_invested * inv.expected_return_percent / 100)}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] tracking-wider uppercase text-slate-500">≈ per month</div>
                    <div className="font-mono-fin text-sm text-slate-900 mt-1">
                      {formatRupees(inv.amount_invested * inv.expected_return_percent / 100 / 12)}
                    </div>
                  </div>
                </>
              )}
              <div className="pt-3 border-t border-red-200/50">
                <div className="text-[10px] tracking-wider uppercase text-slate-500">Actual return</div>
                <div className={`font-mono-fin text-sm mt-1 ${inv.return_percent >= 0 ? "text-green-700" : "text-red-600"}`}>
                  {formatPercent(inv.return_percent)}
                </div>
              </div>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="investors" className="mt-6">
          <ExternalInvestorsPanel investmentId={id} canEdit={canEdit} amountInvested={inv.amount_invested} />
        </TabsContent>

        <TabsContent value="txns" className="mt-6">
          <div className="flex justify-end mb-3">
            {canEdit && (
              <Button
                onClick={() => setTxDialog(true)}
                data-testid="add-transaction-btn"
                className="rounded-none bg-red-600 hover:bg-red-700 text-white gap-2"
              >
                <Plus className="h-4 w-4" /> Add Transaction
              </Button>
            )}
          </div>
          <div className="border border-slate-200 overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="p-3 text-left text-[10px] tracking-wider uppercase text-slate-500">Date</th>
                  <th className="p-3 text-left text-[10px] tracking-wider uppercase text-slate-500">Direction</th>
                  <th className="p-3 text-left text-[10px] tracking-wider uppercase text-slate-500">Category</th>
                  <th className="p-3 text-right text-[10px] tracking-wider uppercase text-slate-500">Amount</th>
                  <th className="p-3 text-left text-[10px] tracking-wider uppercase text-slate-500">Note</th>
                  {canEdit && <th />}
                </tr>
              </thead>
              <tbody>
                {txns.map((t) => (
                  <tr key={t.id} className="border-b border-slate-100">
                    <td className="p-3">{formatDate(t.date)}</td>
                    <td className="p-3">
                      <span className={`text-[10px] tracking-wider uppercase px-2 py-0.5 ${
                        t.direction === "in" ? "bg-green-50 text-green-800" : "bg-red-50 text-red-700"
                      }`}>{t.direction === "in" ? "IN" : "OUT"}</span>
                    </td>
                    <td className="p-3 text-slate-600 capitalize">{t.category.replace(/_/g, " ")}</td>
                    <td className="p-3 text-right font-mono-fin">{formatRupees(t.amount)}</td>
                    <td className="p-3 text-slate-500">{t.note}</td>
                    {canEdit && (
                      <td className="p-3 text-right">
                        <button onClick={() => deleteTx(t.id)} className="text-slate-400 hover:text-red-600">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
                {txns.length === 0 && (
                  <tr><td colSpan={6} className="p-6 text-center text-slate-500">No transactions yet.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="valuations" className="mt-6">
          <ValuationPanel
            investment={inv}
            valuations={vals}
            canEdit={canEdit}
            onOpen={() => setValDialog(true)}
          />
        </TabsContent>

        <TabsContent value="documents" className="mt-6">
          {canEdit && (
            <div className="mb-4">
              <label className="inline-flex items-center gap-2 bg-red-600 text-white px-4 py-2 text-sm font-medium cursor-pointer hover:bg-red-700" data-testid="upload-doc-btn">
                <Upload className="h-4 w-4" /> Upload Document
                <input type="file" className="hidden" onChange={uploadDoc} accept=".pdf,.png,.jpg,.jpeg,.doc,.docx" />
              </label>
            </div>
          )}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {docs.map((d) => (
              <div key={d.id} className="border border-slate-200 p-4 bg-white">
                <div className="text-sm font-medium text-slate-900 truncate">{d.filename}</div>
                <div className="text-xs text-slate-500 mt-1">{formatDate(d.created_at)} · {(d.size / 1024).toFixed(1)} KB</div>
                <div className="mt-3 flex items-center justify-between">
                  <a
                    href={`${API}/documents/${d.id}/download?auth=${token}`}
                    target="_blank" rel="noreferrer"
                    className="inline-flex items-center gap-1 text-sm text-red-600 hover:text-red-700"
                    data-testid={`view-doc-${d.id}`}
                  >
                    View <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                  {canEdit && (
                    <button onClick={() => deleteDoc(d.id)} className="text-slate-400 hover:text-red-600">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
            {docs.length === 0 && (
              <div className="col-span-full text-center text-slate-500 border border-dashed border-slate-200 p-10">
                No documents uploaded yet.
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>

      {/* Transaction dialog */}
      <Dialog open={txDialog} onOpenChange={setTxDialog}>
        <DialogContent data-testid="tx-dialog">
          <DialogHeader><DialogTitle>Add transaction</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Direction</Label>
                <Select value={txForm.direction} onValueChange={(v) => setTxForm({ ...txForm, direction: v })}>
                  <SelectTrigger className="mt-2" data-testid="tx-direction"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="in">IN (money received)</SelectItem>
                    <SelectItem value="out">OUT (money paid)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Date</Label>
                <Input type="date" value={txForm.date} onChange={(e) => setTxForm({ ...txForm, date: e.target.value })} className="mt-2" data-testid="tx-date" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Amount (Rs)</Label>
                <Input type="number" value={txForm.amount} onChange={(e) => setTxForm({ ...txForm, amount: e.target.value })} className="mt-2" data-testid="tx-amount" />
              </div>
              <div>
                <Label>Category</Label>
                <Select value={txForm.category} onValueChange={(v) => setTxForm({ ...txForm, category: v })}>
                  <SelectTrigger className="mt-2" data-testid="tx-category"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c.replace(/_/g, " ")}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Note</Label>
              <Textarea value={txForm.note} onChange={(e) => setTxForm({ ...txForm, note: e.target.value })} className="mt-2" data-testid="tx-note" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTxDialog(false)} className="rounded-none">Cancel</Button>
            <Button onClick={addTx} className="rounded-none bg-red-600 hover:bg-red-700 text-white" data-testid="tx-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Valuation dialog */}
      <ValuationDialog
        open={valDialog}
        onOpenChange={setValDialog}
        investment={inv}
        form={valForm}
        setForm={setValForm}
        onSave={addVal}
      />
    </div>
  );
}

/* ---------------- External Investors Panel ---------------- */
function ExternalInvestorsPanel({ investmentId, canEdit, amountInvested }) {
  const [list, setList] = useState([]);
  const [dlg, setDlg] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", phone: "", amount_invested: "", share_percent: "", note: "" });

  const load = () => api.get(`/investments/${investmentId}/external-investors`).then((r) => setList(r.data));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [investmentId]);

  const open = (e = null) => {
    setEditing(e);
    setForm(e || { name: "", email: "", phone: "", amount_invested: "", share_percent: "", note: "" });
    setDlg(true);
  };

  const save = async () => {
    try {
      const payload = {
        ...form,
        amount_invested: Number(form.amount_invested || 0),
        share_percent: Number(form.share_percent || 0),
      };
      if (editing) {
        await api.put(`/investments/${investmentId}/external-investors/${editing.id}`, payload);
      } else {
        await api.post(`/investments/${investmentId}/external-investors`, payload);
      }
      toast.success(editing ? "Updated" : "External investor added");
      setDlg(false);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this external investor?")) return;
    await api.delete(`/investments/${investmentId}/external-investors/${id}`);
    toast.success("Removed");
    await load();
  };

  const totalExternalPct = list.reduce((a, e) => a + (e.share_percent || 0), 0);
  const marksamPct = Math.max(0, 100 - totalExternalPct);
  const totalExternalRs = list.reduce((a, e) => a + (e.amount_invested || 0), 0);

  return (
    <div className="space-y-4">
      <div className="grid gap-3 sm:grid-cols-3">
        <div className="border border-slate-200 bg-white rounded-md p-4">
          <div className="eyebrow">MARKSAM share</div>
          <div className="mt-1 font-mono-fin text-2xl text-red-600">{marksamPct.toFixed(2)}%</div>
        </div>
        <div className="border border-slate-200 bg-white rounded-md p-4">
          <div className="eyebrow">External share</div>
          <div className="mt-1 font-mono-fin text-2xl text-slate-900">{totalExternalPct.toFixed(2)}%</div>
        </div>
        <div className="border border-slate-200 bg-white rounded-md p-4">
          <div className="eyebrow">External capital</div>
          <div className="mt-1 font-mono-fin text-2xl text-slate-900">{formatRupees(totalExternalRs)}</div>
        </div>
      </div>

      {canEdit && (
        <div className="flex justify-end">
          <Button onClick={() => open()} className="rounded-md bg-red-600 hover:bg-red-700 text-white gap-2" data-testid="add-external-btn">
            <UserPlus className="h-4 w-4" /> Add external investor
          </Button>
        </div>
      )}

      {list.length === 0 ? (
        <div className="border border-dashed border-slate-200 rounded-md p-10 text-center text-slate-500">
          No external investors on this project.<br />
          <span className="text-xs">MARKSAM owns 100% of this investment.</span>
        </div>
      ) : (
        <div className="grid gap-3 md:grid-cols-2">
          {list.map((e) => (
            <div key={e.id} className="border border-slate-200 bg-white rounded-md p-4" data-testid={`external-${e.id}`}>
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="font-medium text-slate-900 truncate">{e.name}</div>
                  <div className="text-xs text-slate-500 truncate">{e.email || e.phone || "—"}</div>
                </div>
                {canEdit && (
                  <div className="flex items-center gap-1 shrink-0">
                    <button onClick={() => open(e)} className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-md">
                      <Pencil className="h-3.5 w-3.5" />
                    </button>
                    <button onClick={() => remove(e.id)} className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-slate-100 rounded-md">
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-2 gap-3 mt-3 pt-3 border-t border-slate-100 text-[13px]">
                <div>
                  <div className="text-[10px] tracking-wider uppercase text-slate-400">Amount</div>
                  <div className="font-mono-fin text-slate-900 mt-0.5">{formatRupees(e.amount_invested)}</div>
                </div>
                <div>
                  <div className="text-[10px] tracking-wider uppercase text-slate-400">Share</div>
                  <div className="font-mono-fin text-slate-900 mt-0.5">{e.share_percent}%</div>
                </div>
              </div>
              {e.note && <div className="text-xs text-slate-500 mt-2 pt-2 border-t border-slate-100">{e.note}</div>}
            </div>
          ))}
        </div>
      )}

      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent>
          <DialogHeader><DialogTitle>{editing ? "Edit external investor" : "Add external investor"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-2" data-testid="ext-name" /></div>
              <div><Label>Email</Label><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-2" /></div>
              <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-2" /></div>
              <div><Label>Amount (Rs)</Label><Input type="number" value={form.amount_invested} onChange={(e) => setForm({ ...form, amount_invested: e.target.value })} className="mt-2" data-testid="ext-amount" /></div>
              <div><Label>Share % of this project</Label><Input type="number" step="0.01" value={form.share_percent} onChange={(e) => setForm({ ...form, share_percent: e.target.value })} className="mt-2" data-testid="ext-share" /></div>
            </div>
            <div><Label>Note</Label><Textarea value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="mt-2" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlg(false)} className="rounded-md">Cancel</Button>
            <Button onClick={save} disabled={!form.name} className="rounded-md bg-red-600 hover:bg-red-700 text-white" data-testid="ext-save">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

